import { useCallback, useEffect, useState } from 'react';

/**
 * A countdown in whole seconds, from a DEADLINE rather than a decrement.
 *
 * A `setTimeout(…, 1000)` chain loses time whenever the tab is throttled in
 * the background, and these waits are long (ten minutes before a second code,
 * six hours before a third): a countdown that drifts by minutes would tell
 * somebody they may ask again when the server will still refuse. The clock is
 * read against the deadline on every tick, so a returning tab is right at its
 * first one. `start(0)` clears it.
 */
export function useCountdown(): { left: number; start: (seconds: number) => void } {
  const [deadline, setDeadline] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadline <= Date.now()) return;
    const id = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (n >= deadline) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, [deadline]);

  const start = useCallback((seconds: number) => {
    const n = Date.now();
    setNow(n);
    setDeadline(seconds > 0 ? n + seconds * 1000 : 0);
  }, []);

  return { left: Math.max(0, Math.ceil((deadline - now) / 1000)), start };
}
